<div class="fixed inset-0 z-40 flex">
  <div class="flex-1 bg-black/30" (click)="closePanel.emit()" aria-hidden="true"></div>

  <aside
    class="w-full max-w-2xl bg-white h-full shadow-2xl overflow-y-auto border-l border-gray-200 flex flex-col"
    appModalDialog
    (dialogClose)="closePanel.emit()"
    role="dialog"
    aria-modal="true"
    aria-label="AI Ticket"
  >
    <!-- Header -->
    <div class="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
      <div class="flex items-center gap-2">
        <i class="ri-ticket-2-line text-primary text-lg"></i>
        <h2 class="text-base font-semibold text-gray-800">AI Ticket</h2>
        @if (ticket) {
          <span class="text-xs text-gray-500">
            {{ ticket.model }} · skill v{{ ticket.skill_version }}
          </span>
        }
      </div>
      <div class="flex items-center gap-2">
        @if (step === 'result' && !generating) {
          <button class="btn btn-xs btn-ghost" (click)="backToInput()" title="Back to inputs">
            <i class="ri-arrow-left-line"></i>
            Back
          </button>
          <button
            class="btn btn-xs btn-ghost"
            (click)="regenerate()"
            title="Regenerate (force fresh call)"
          >
            <i class="ri-refresh-line"></i>
            Regenerate
          </button>
        }
        <button class="btn btn-xs btn-ghost" (click)="closePanel.emit()" aria-label="Close">
          <i class="ri-close-line text-lg"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="flex-1 p-4 space-y-4">
      @if (step === 'input') {
        <!-- INPUT FORM -->
        @if (loadingSkills) {
          <div class="relative min-h-[120px]">
            <ngx-spinner
              size="medium"
              color="#cd0005"
              bdColor="transparent"
              [fullScreen]="false"
            ></ngx-spinner>
          </div>
        } @else if (skillsError) {
          <div role="alert" class="alert alert-error text-sm">
            <i class="ri-error-warning-line"></i>
            <span>{{ skillsError }}</span>
          </div>
        } @else if (skills.length === 0) {
          <div role="alert" class="alert alert-warning text-sm">
            <i class="ri-information-line"></i>
            <span>
              No ticket templates available. Go to
              <a [routerLink]="['/admin/settings/templates']" class="underline">
                Settings → Ticket Templates
              </a>
              to create one.
            </span>
          </div>
        } @else {
          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-700" for="skill-picker">Skill</label>
            <select
              id="skill-picker"
              class="w-full pl-3 pr-8 py-1 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white text-gray-900"
              [(ngModel)]="selectedSkillId"
              [disabled]="generating"
            >
              @for (s of skills; track s.id) {
                <option [ngValue]="s.id">{{ s.name }}</option>
              }
            </select>
            @if (activeSkill?.description) {
              <p class="text-xs text-gray-500">{{ activeSkill?.description }}</p>
            }
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium text-gray-700" for="guidance">
              Support guidance
              <span class="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="guidance"
              class="w-full pl-3 pr-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white text-gray-900 placeholder-gray-400"
              rows="4"
              [(ngModel)]="supportGuidance"
              [disabled]="generating"
              placeholder="e.g. Agent reported the booking failed after payment. Check if OrderCreate returned a provider rejection."
            ></textarea>
          </div>

          @if (generationError) {
            <div role="alert" class="alert alert-error text-sm">
              <i class="ri-error-warning-line"></i>
              <span>{{ generationError }}</span>
            </div>
          }

          <div class="flex justify-end">
            <button
              class="btn btn-sm btn-primary"
              (click)="generate(false)"
              [disabled]="generating || selectedSkillId === null"
            >
              @if (generating) {
                <span class="loading loading-spinner loading-xs mr-1"></span>
              } @else {
                <i class="ri-sparkling-line mr-1"></i>
              }
              Generate
            </button>
          </div>

          @if (generating) {
            <div class="relative min-h-[80px]">
              <ngx-spinner
                size="medium"
                color="#cd0005"
                bdColor="transparent"
                [fullScreen]="false"
              ></ngx-spinner>
              <div class="absolute inset-0 flex items-end justify-center pb-2">
                <span class="text-xs text-gray-500">Drafting ticket — up to 30s…</span>
              </div>
            </div>
          }
        }
      } @else {
        <!-- RESULT VIEW -->
        @if (generating) {
          <div class="relative min-h-[200px]">
            <ngx-spinner
              size="medium"
              color="#cd0005"
              bdColor="transparent"
              [fullScreen]="false"
            ></ngx-spinner>
          </div>
        } @else if (generationError) {
          <div role="alert" class="alert alert-error text-sm">
            <i class="ri-error-warning-line"></i>
            <span>{{ generationError }}</span>
          </div>
        } @else if (ticket) {
          <!-- Title + severity/labels chip row -->
          @if (ticket.result['title']) {
            <h3 class="text-lg font-semibold text-gray-900 leading-tight">
              {{ ticket.result['title'] }}
            </h3>
          }
          <div class="flex flex-wrap gap-2 items-center">
            @if (ticket.result['severity']) {
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium uppercase"
                [ngClass]="severityClasses(ticket.result['severity'])"
              >
                {{ ticket.result['severity'] }}
              </span>
            }
            @if (asStringArray(ticket.result['labels']).length) {
              @for (lbl of asStringArray(ticket.result['labels']); track lbl) {
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700"
                >
                  {{ lbl }}
                </span>
              }
            }
          </div>

          <!-- Generic renderer for the rest -->
          <div class="space-y-4">
            @for (entry of orderedResultEntries; track entry[0]) {
              @if (entry[0] !== 'title' && entry[0] !== 'severity' && entry[0] !== 'labels') {
                <section>
                  <h4 class="text-sm font-semibold text-gray-700 mb-1">{{ humanise(entry[0]) }}</h4>
                  @switch (kindOf(entry[1])) {
                    @case ('string') {
                      <div
                        class="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed"
                      >
                        {{ asString(entry[1]) }}
                      </div>
                    }
                    @case ('string-array') {
                      <ul class="list-disc pl-5 space-y-1 text-sm text-gray-800">
                        @for (item of asStringArray(entry[1]); track item) {
                          <li class="whitespace-pre-wrap">{{ item }}</li>
                        }
                      </ul>
                    }
                    @default {
                      <pre class="text-xs bg-gray-50 rounded p-2 overflow-x-auto">{{
                        asOther(entry[1])
                      }}</pre>
                    }
                  }
                </section>
              }
            }
          </div>

          <!-- Action bar -->
          <div
            class="sticky bottom-0 -mx-4 -mb-4 px-4 py-2 bg-white border-t border-gray-200 flex flex-wrap gap-2 items-center"
          >
            <button class="btn btn-sm btn-outline" (click)="copyMarkdown()">
              <i class="ri-file-copy-line mr-1"></i>
              Copy as Markdown
            </button>
            <button class="btn btn-sm btn-outline" (click)="downloadMarkdown()">
              <i class="ri-download-line mr-1"></i>
              Download .md
            </button>

            <!-- GitHub: flips to a link once the issue exists -->
            @if (ticket.github_issue_url) {
              <a
                class="btn btn-sm btn-success"
                [href]="ticket.github_issue_url"
                target="_blank"
                rel="noopener"
                title="Open the GitHub issue created from this ticket"
              >
                <i class="ri-github-fill mr-1"></i>
                Show GH Issue
                @if (ticket.github_issue_number) {
                  &nbsp;#{{ ticket.github_issue_number }}
                }
              </a>
            } @else if (githubConnected) {
              <button
                class="btn btn-sm btn-primary"
                (click)="createIssue()"
                [disabled]="creatingIssue"
                title="Create a GitHub issue in the connected repository"
              >
                @if (creatingIssue) {
                  <span class="loading loading-spinner loading-xs mr-1"></span>
                } @else {
                  <i class="ri-github-fill mr-1"></i>
                }
                Create GH Issue
              </button>
            }
            @if (issueError) {
              <span class="text-xs text-red-600">{{ issueError }}</span>
            }
            @if (copyFeedback) {
              <span class="text-xs text-gray-500">{{ copyFeedback }}</span>
            }
            @if (ticket.latency_ms || ticket.input_tokens) {
              <span class="ml-auto text-xs text-gray-400">
                @if (ticket.latency_ms) {
                  {{ ticket.latency_ms }} ms
                }
                @if (ticket.input_tokens) {
                  · {{ ticket.input_tokens }} in · {{ ticket.output_tokens }} out tokens
                }
              </span>
            }
          </div>
        }
      }
    </div>
  </aside>
</div>
